import { StrictMode, Suspense, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useRoutes } from "react-router-dom";

import "./styles.css";
import "./i18n";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initObservability } from "@/lib/observability";
import { installStaleChunkGuard } from "@/lib/stale-chunk-guard";
import { dropLegacyOfflineData, retireServiceWorkers } from "@/lib/legacyCleanup";
import { DialogHost } from "@/lib/dialog";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TenantProvider } from "@/lib/tenant";
import { PlanProvider } from "@/lib/plan";
import { PrefsProvider } from "@/lib/prefs";
import { ImpersonationProvider } from "@/lib/impersonation";
import { appRoutes } from "@/app/routes";

installStaleChunkGuard();
void initObservability();
// Web offline mode and the per-app service workers are gone (spec §4, §7).
void retireServiceWorkers().catch(() => {});
void dropLegacyOfflineData();

function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
      <div className="kicker">Loading…</div>
    </div>
  );
}

function ImpersonationProviderBridge({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <ImpersonationProvider user={user}>{children}</ImpersonationProvider>;
}

function AppRoutes() {
  const { loading } = useAuth();
  const element = useRoutes(appRoutes);
  if (loading) return <Loading />;
  return <Suspense fallback={<Loading />}>{element}</Suspense>;
}

const mount = document.getElementById("app");
if (!mount) throw new Error("main: no #app mount node");

createRoot(mount).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <PrefsProvider>
          <AuthProvider>
            <ImpersonationProviderBridge>
              <TenantProvider>
                <PlanProvider>
                  <AppRoutes />
                  <DialogHost />
                </PlanProvider>
              </TenantProvider>
            </ImpersonationProviderBridge>
          </AuthProvider>
        </PrefsProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
